import * as ex from 'excalibur';
import { ActionType, Allegiance, ControlType } from '../types/enums';
import { Action, GridPosition } from '../types/interfaces';
import { TILE_SIZE } from '../config/constants';
import { Unit } from '../core/unit';
import { Grid } from '../core/grid';
import { PlayerUnit } from './playerUnit';

// Rogue class that can move diagonally and tries to backstab
export class Rogue extends Unit {
  constructor(options: {
    gridX: number,
    gridY: number,
    allegiance: Allegiance,
    initiative: number,
    controlType?: ControlType,
    grid: Grid
  }) {
    super({
      pos: new ex.Vector(options.gridX * TILE_SIZE + TILE_SIZE / 2, options.gridY * TILE_SIZE + TILE_SIZE / 2),
      gridX: options.gridX,
      gridY: options.gridY,
      type: 'rogue',
      name: 'Rogue',
      width: TILE_SIZE * 0.7,
      height: TILE_SIZE * 0.7,
      color: options.allegiance === Allegiance.Player ? ex.Color.Yellow : ex.Color.Orange,
      initiative: options.initiative,
      allegiance: options.allegiance,
      health: 25,
      maxHealth: 25,
      controlType: options.controlType || (options.allegiance === Allegiance.Player ?
        ControlType.Manual : ControlType.Automatic),
      moveRange: 1,
      attackRange: 1,
      abilities: ['backstab', 'diagonal_move'],
      grid: options.grid
    });

    // Set the emoji for this unit type
    this.setEmoji("🗡️"); // Dagger for rogue/sneaky type
  }

  public decideAction(): Action | null {
    // If manual control and player unit, delegate to player input
    if (this.controlType === ControlType.Manual && this instanceof PlayerUnit) {
      return super.decideAction();
    }

    // Check for enemies in attack range
    const attackTargets = this.findTargetsInRange(this.attackRange);
    if (attackTargets.length > 0) {
      // Attack the closest enemy
      return this.createAttackAction(attackTargets[0]);
    }

    // Try to find a backstab position (position on the opposite side of a target from its ally)
    const backstabPosition = this.findBackstabPosition();
    if (backstabPosition) {
      return this.createMoveAction(backstabPosition.x, backstabPosition.y);
    }

    // If no backstab position, try to move diagonally toward the closest enemy
    const closestEnemy = this.findClosestEnemy();
    if (closestEnemy) {
      // Favor diagonal movement
      const diagonalMoves = [
        { x: this.gridX + 1, y: this.gridY + 1 },
        { x: this.gridX + 1, y: this.gridY - 1 },
        { x: this.gridX - 1, y: this.gridY + 1 },
        { x: this.gridX - 1, y: this.gridY - 1 }
      ];

      // Sort moves by distance to the closest enemy
      diagonalMoves.sort((a, b) => {
        const distA = Math.abs(a.x - closestEnemy.gridX) + Math.abs(a.y - closestEnemy.gridY);
        const distB = Math.abs(b.x - closestEnemy.gridX) + Math.abs(b.y - closestEnemy.gridY);
        return distA - distB;
      });

      // Try diagonal moves first
      for (const move of diagonalMoves) {
        if (this.grid.isValidMove(move.x, move.y)) {
          return this.createMoveAction(move.x, move.y);
        }
      }

      // If no diagonal moves, try cardinal moves
      const cardinalMoves = [
        { x: this.gridX + 1, y: this.gridY },
        { x: this.gridX - 1, y: this.gridY },
        { x: this.gridX, y: this.gridY + 1 },
        { x: this.gridX, y: this.gridY - 1 }
      ];

      // Sort by distance to the closest enemy
      cardinalMoves.sort((a, b) => {
        const distA = Math.abs(a.x - closestEnemy.gridX) + Math.abs(a.y - closestEnemy.gridY);
        const distB = Math.abs(b.x - closestEnemy.gridX) + Math.abs(b.y - closestEnemy.gridY);
        return distA - distB;
      });

      for (const move of cardinalMoves) {
        if (this.grid.isValidMove(move.x, move.y)) {
          return this.createMoveAction(move.x, move.y);
        }
      }
    }

    // If no moves possible, wait
    return this.createWaitAction();
  }

  // Find a position for backstabbing (where an enemy is between the rogue and another enemy)
  private findBackstabPosition(): GridPosition | null {
    const enemies = this.grid.getActorsByAllegiance(
      this.allegiance === Allegiance.Player ? Allegiance.Enemy : Allegiance.Player
    );

    if (enemies.length < 2) return null; // Need at least 2 enemies for backstab

    // Check each enemy
    for (const enemy of enemies) {
      // Find other enemies that could create a backstab opportunity
      const otherEnemies = enemies.filter(e => e !== enemy);

      for (const otherEnemy of otherEnemies) {
        // Calculate the opposite position from otherEnemy through enemy
        const dx = enemy.gridX - otherEnemy.gridX;
        const dy = enemy.gridY - otherEnemy.gridY;

        // Position on the other side of the enemy
        const backstabX = enemy.gridX + Math.sign(dx);
        const backstabY = enemy.gridY + Math.sign(dy);

        // Check if the position is valid
        if (this.grid.isValidMove(backstabX, backstabY)) {
          // Check if this position is reachable within the rogue's move range
          const distance = Math.abs(backstabX - this.gridX) + Math.abs(backstabY - this.gridY);
          if (distance <= this.moveRange) {
            return { x: backstabX, y: backstabY };
          }
        }
      }
    }

    return null;
  }
}